import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'
import { UniversalSwipe } from '../../utils/swipe'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/wizard?tab=designer).
 *
 * @slot default - Slot para os painéis de etapa (br-wizard-panel) do wizard.
 */
@Component({
  tag: 'br-wizard',
  styleUrl: 'wizard.scss',
  shadow: true,
})
export class Wizard {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el!: HTMLBrWizardElement

  /**
   * Internals API para suporte a formulários nativos.
   */
  @AttachInternals() elementInternals: ElementInternals

  /**
   * Índice da etapa inicial do wizard.
   * Define qual etapa será exibida ao carregar o componente.
   * A numeração começa em 1 (primeira etapa = 1, segunda = 2, etc).
   */
  @Prop({ reflect: true }) initialStep: number = 1

  /**
   * Orientação visual do wizard.
   * Define se os indicadores de progresso serão exibidos horizontalmente ou verticalmente.
   */
  @Prop({ reflect: true }) orientation: 'horizontal' | 'vertical' = 'horizontal'

  /**
   * Tipo de progressão entre etapas.
   * - `linear`: O usuário deve completar cada etapa sequencialmente. Não é possível pular etapas futuras.
   * - `nonlinear`: O usuário pode navegar livremente entre qualquer etapa.
   */
  @Prop({ reflect: true }) progressionType: 'linear' | 'nonlinear' = 'nonlinear'

  /**
   * ID customizado para o componente.
   * Se não fornecido, um ID único será gerado automaticamente.
   */
  @Prop({ reflect: true }) customId: string = Helpers.generateUniqueId()

  /**
   * Etapa atual sendo exibida.
   * Gerenciado internamente pelo componente.
   */
  @State() currentStep: number = 1

  /** Estado mobile para detecção responsiva */
  @State() isMobile: boolean = false

  /**
   * Estado collapsed para recolher/expandir barra de progresso (mobile)
   */
  @State() collapsed: boolean = false

  /**
   * Lista de painéis filhos (br-wizard-panel).
   * Descobertos automaticamente via slot.
   */
  @State() panels: HTMLElement[] = []

  /**
   * Mensagem de status para leitores de tela (aria-live)
   */
  @State() ariaLiveMessage: string = ''

  // ========================================
  // EVENTOS
  // ========================================

  /**
   * Evento emitido APÓS mudar de etapa com sucesso.
   *
   * Este evento é disparado independente da origem da navegação:
   * - Botões "Avançar" ou "Voltar"
   * - Click direto em um step do indicador de progresso
   * - Chamadas programáticas via métodos públicos (goToStep, nextStep, etc)
   */
  @Event() brWizardStepChange: EventEmitter<number>

  /**
   * Evento emitido ANTES de mudar de etapa (permite validação e cancelamento).
   *
   * Comportamento:
   * - Disparado apenas ao AVANÇAR (targetStep > currentStep)
   * - Não é disparado ao VOLTAR (targetStep < currentStep)
   * - Funciona tanto para cliques nos botões quanto para cliques diretos nos steps
   * - Pode ser cancelado com `event.preventDefault()` para bloquear a navegação
   */
  @Event({ cancelable: true }) brWizardBeforeStepChange: EventEmitter<number>

  /**
   * Evento emitido ao concluir o wizard (última etapa).
   */
  @Event() brWizardComplete: EventEmitter<void>

  /**
   * Evento emitido ao cancelar o wizard.
   */
  @Event() brWizardCancel: EventEmitter<void>

  /**
   * Evento emitido quando uma ação é bloqueada (validação falha, progressão linear impedida).
   * Emite uma string indicando o motivo: 'linear-progression' ou 'validation-failed'.
   * Use getCurrentStepIndex() para obter o contexto da etapa atual.
   */
  @Event() brWizardNavigationBlocked: EventEmitter<string>

  // ========================================
  // PROPRIEDADES PRIVADAS
  // ========================================

  /**
   * Referência ao componente br-step.
   */
  private stepRef: HTMLBrStepElement | null = null

  /**
   * Flag para evitar loop quando sincronizamos programaticamente com br-step.
   */
  private isSyncingWithStep: boolean = false

  /**
   * Instância do Swipe para gestos mobile na barra de progresso.
   */
  private progressBarSwipe: UniversalSwipe | null = null

  /**
   * Referência ao elemento DOM da barra de progresso.
   */
  private progressBarRef: HTMLElement | null = null

  /**
   * Referência ao elemento DOM do formulário/painel ativo.
   */
  private formRef: HTMLElement | null = null

  /**
   * Timer para limpar mensagens aria-live.
   */
  private ariaLiveTimeout: number | null = null

  // ========================================
  // CONSTANTES
  // ========================================

  /**
   * Tempo de espera para limpar mensagens aria-live (em ms).
   */
  private readonly ARIA_LIVE_CLEANUP_DELAY = 5000

  /**
   * Seletor de elementos focáveis dentro dos painéis.
   */
  private readonly FOCUSABLE_SELECTOR =
    'br-button, button, [href], br-item, br-input, input, br-select, select, br-textarea, textarea, [tabindex]:not([tabindex="-1"])'

  // ========================================
  // WATCHERS
  // ========================================

  /**
   * Observa mudanças no tipo de progressão.
   * Atualiza a navegação dos painéis quando muda entre linear/nonlinear.
   */
  @Watch('progressionType')
  handleProgressionTypeChange() {
    this.updatePanels()
  }

  /**
   * Observa mudanças na etapa inicial.
   * Reinicializa o wizard quando a prop initialStep é alterada externamente.
   */
  @Watch('initialStep')
  async handleInitialStepChange() {
    this.setInitialStep()
    await this.syncWithBrStep()
    this.updatePanels()
  }

  /**
   * Observa mudanças na etapa atual (fonte única de verdade).
   * Sincroniza com br-step, atualiza painéis, anuncia mudança e move foco.
   * @param newStep - Nova etapa
   * @param oldStep - Etapa anterior
   */
  @Watch('currentStep')
  async handleCurrentStepChange(newStep: number, oldStep: number) {
    if (newStep !== oldStep) {
      await this.syncWithBrStep()
      this.updatePanels()

      this.announceStepChange(newStep, oldStep)

      this.setFocusOnActivePanel()
    }
  }

  // ========================================
  // LISTENERS
  // ========================================

  /**
   * Detecta quando o slot muda (painéis adicionados/removidos dinamicamente).
   * Redescobre os painéis e ressincroniza o estado.
   * @param event - Evento slotchange
   */
  @Listen('slotchange')
  async handleSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement
    this.discoverPanelsFromSlot(slot)
    await this.syncWithBrStep()
    this.updatePanels()
  }

  /**
   * Detecta quando o br-step muda de etapa ativa.
   * Valida e sincroniza a navegação com o componente interno br-step.
   * @param event - Evento customizado do br-step
   */
  @Listen('brStepChange')
  async handleStepChange(event: CustomEvent<{ activeStep: number }>) {
    if (this.isSyncingWithStep) {
      return
    }

    const requestedStep = event.detail.activeStep + 1
    const success = await this.navigateToStep(requestedStep)

    if (!success) {
      event.stopPropagation()
      await this.syncWithBrStep()
    }
  }

  // ========================================
  // LIFECYCLE HOOKS
  // ========================================

  /**
   * Lifecycle: inicializa painéis antes da renderização.
   */
  componentWillLoad() {
    this.panels = Array.from(this.el.querySelectorAll('br-wizard-panel'))
    this.setInitialStep()
  }

  /**
   * Lifecycle: carrega painéis e configura estado inicial.
   * Sincroniza com br-step, atualiza painéis e configura detecção mobile.
   */
  async componentDidLoad() {
    const slot = this.el.shadowRoot?.querySelector('slot')
    if (slot !== null && typeof slot !== 'undefined') {
      const assignedElements = slot.assignedElements()
      const slotPanels = assignedElements.filter(
        (el) => el.tagName.toLowerCase() === 'br-wizard-panel'
      ) as HTMLElement[]

      if (slotPanels.length !== this.panels.length) {
        this.panels = slotPanels
      }
    }

    await this.syncWithBrStep()
    this.updatePanels()
    this.setupMobileDetection()

    if (this.isMobile) {
      this.collapsed = true
    }
  }

  /**
   * Lifecycle: limpa listeners quando componente é removido.
   */
  disconnectedCallback() {
    this.cleanupMobileDetection()
    this.cleanupSwipeListeners()
    this.clearAriaLiveTimeout()
  }

  // ========================================
  // MÉTODOS PÚBLICOS
  // ========================================

  /**
   * Avança para a próxima etapa.
   * Executa validação via evento `brWizardBeforeStepChange` antes de navegar.
   * @returns Promise<boolean> - true se navegou com sucesso, false se bloqueado
   */
  @Method()
  async nextStep(): Promise<boolean> {
    return await this.navigateToStep(this.currentStep + 1)
  }

  /**
   * Volta para a etapa anterior.
   * Não executa validação ao retornar.
   * @returns Promise<boolean> - true se navegou com sucesso, false se bloqueado
   */
  @Method()
  async previousStep(): Promise<boolean> {
    return await this.navigateToStep(this.currentStep - 1)
  }

  /**
   * Navega para uma etapa específica.
   * Executa validação se estiver avançando (etapa alvo > etapa atual).
   * @param stepNumber - Número da etapa de destino (numeração começa em 1)
   * @returns Promise<boolean> - true se navegou com sucesso, false se bloqueado
   */
  @Method()
  async goToStep(stepNumber: number): Promise<boolean> {
    return await this.navigateToStep(stepNumber)
  }

  /**
   * Retorna à primeira etapa do wizard.
   * Não executa validação (útil para reiniciar o fluxo).
   * @returns Promise<void>
   */
  @Method()
  async reset(): Promise<void> {
    await this.navigateToStep(1, true)
  }

  /**
   * Retorna o elemento HTML do painel ativo no momento.
   * @returns Promise<HTMLElement | null>
   */
  @Method()
  async getCurrentStep(): Promise<HTMLElement | null> {
    return this.panels[this.currentStep - 1] || null
  }

  /**
   * Retorna todos os painéis do wizard.
   * @returns Promise<HTMLElement[]>
   */
  @Method()
  async getAllSteps(): Promise<HTMLElement[]> {
    return [...this.panels]
  }

  /**
   * Retorna um painel específico pelo índice.
   * @param stepNumber - Número da etapa (numeração começa em 1)
   * @returns Promise<HTMLElement | null>
   */
  @Method()
  async getStepByIndex(stepNumber: number): Promise<HTMLElement | null> {
    return this.panels[stepNumber - 1] || null
  }

  /**
   * Retorna o índice (número) da etapa atual.
   * A numeração começa em 1 (primeira etapa = 1, segunda = 2, etc).
   * @returns Promise<number>
   */
  @Method()
  async getCurrentStepIndex(): Promise<number> {
    return this.currentStep
  }

  /**
   * Retorna o número total de etapas do wizard.
   * @returns Promise<number>
   */
  @Method()
  async getTotalSteps(): Promise<number> {
    return this.panels.length
  }

  // ========================================
  // NAVEGAÇÃO E VALIDAÇÃO
  // ========================================

  /**
   * Método privado central que gerencia toda a navegação com validação.
   *
   * Comportamento unificado para linear e nonlinear:
   * - Linear: Valida ao avançar, não permite pular steps
   * - Nonlinear: Valida ao avançar, permite pular steps livremente
   *
   * @param targetStep - Número da etapa de destino (numeração começa em 1)
   * @param skipValidation - Se true, pula validação (útil para reset/programático)
   * @returns Promise<boolean> - true se navegou com sucesso, false se bloqueado
   */
  private async navigateToStep(targetStep: number, skipValidation: boolean = false): Promise<boolean> {
    /**
     * VALIDAÇÕES BÁSICAS
     * 1. Verifica se targetStep está dentro dos limites válidos
     * 2. Se já estamos na etapa alvo, retorna sucesso imediatamente
     */
    if (targetStep < 1 || targetStep > this.panels.length) {
      return false
    }

    if (targetStep === this.currentStep) {
      return true
    }

    /**
     * MODO LINEAR: RESTRIÇÕES
     * No modo linear, não é permitido pular para etapas futuras não visitadas.
     * É possível voltar, mas não avançar além da próxima etapa sequencial.
     */
    if (this.progressionType === 'linear') {
      if (targetStep > this.currentStep + 1) {
        const message = `Não é possível pular etapas. Complete a etapa ${this.currentStep + 1} antes de avançar.`

        this.announceToScreenReader(message)

        this.brWizardNavigationBlocked.emit('linear-progression')

        return false
      }
    }

    /**
     * VALIDAÇÃO CUSTOMIZADA
     * Emite evento brWizardBeforeStepChange APENAS se:
     * 1. Não estamos pulando validação (skipValidation=false)
     * 2. Estamos AVANÇANDO (targetStep > currentStep)
     *
     * Observações:
     * - Voltar não precisa validar
     * - Validação se aplica a ambos os modos (linear e nonlinear)
     */
    const shouldValidate = !skipValidation && targetStep > this.currentStep

    if (shouldValidate) {
      const eventResult = this.brWizardBeforeStepChange.emit(targetStep)

      /**
       * Se preventDefault() foi chamado no listener do evento,
       * bloqueia a navegação e retorna false
       */
      if (eventResult.defaultPrevented) {
        const message = 'Não é possível avançar. Verifique os campos obrigatórios da etapa atual.'

        this.announceToScreenReader(message)

        this.brWizardNavigationBlocked.emit('validation-failed')

        return false
      }
    }

    /**
     * EXECUTA NAVEGAÇÃO
     * Atualiza o estado interno e emite evento de confirmação
     */
    this.currentStep = targetStep

    this.brWizardStepChange.emit(this.currentStep)

    return true
  }

  // ========================================
  // SINCRONIZAÇÃO COM BR-STEP
  // ========================================

  /**
   * Sincroniza estado do wizard com o br-step.
   *
   * O br-step já gerencia completamente os atributos 'active' via componentShouldUpdate
   * e requestAnimationFrame, então apenas chamamos os métodos de navegação dele.
   */
  private async syncWithBrStep() {
    if (this.stepRef === null) return

    if (typeof this.stepRef.GetActiveStep !== 'function') {
      return
    }

    this.isSyncingWithStep = true

    try {
      const targetStepIndex = this.currentStep - 1
      const currentActiveStep = await this.stepRef.GetActiveStep()

      if (currentActiveStep !== targetStepIndex) {
        const diff = targetStepIndex - currentActiveStep

        if (diff > 0) {
          for (let i = 0; i < diff; i++) {
            await this.stepRef.ProceedToNextStep()
          }
        } else {
          for (let i = 0; i < Math.abs(diff); i++) {
            await this.stepRef.BackToPreviousStep()
          }
        }
      }
    } finally {
      this.isSyncingWithStep = false
    }
  }

  // ========================================
  // GERENCIAMENTO DE PAINÉIS
  // ========================================

  /**
   * Descobre painéis filhos usando assigned elements do slot.
   * @param slot - Elemento slot do shadow DOM
   */
  private discoverPanelsFromSlot(slot: HTMLSlotElement) {
    const assignedElements = slot.assignedElements()
    this.panels = assignedElements.filter((el) => el.tagName.toLowerCase() === 'br-wizard-panel') as HTMLElement[]
  }

  /**
   * Atualiza estados de todos os painéis.
   * Define o índice e o estado ativo de cada painel.
   */
  private async updatePanels() {
    for (let idx = 0; idx < this.panels.length; idx++) {
      const panel = this.panels[idx] as HTMLBrWizardPanelElement
      const stepNumber = idx + 1
      const isActive = stepNumber === this.currentStep

      await panel.setIndex(stepNumber)
      await panel.setActive(isActive)
    }
  }

  /**
   * Define etapa inicial baseada na prop initialStep.
   * Garante que o valor esteja dentro dos limites válidos.
   */
  private setInitialStep() {
    this.currentStep = Math.max(1, Math.min(this.initialStep, this.panels.length))
  }

  // ========================================
  // ACESSIBILIDADE
  // ========================================

  /**
   * Anuncia mensagem para leitores de tela via aria-live.
   * Limpa automaticamente após 5 segundos para evitar poluição.
   * @param message - Mensagem a ser anunciada
   */
  private announceToScreenReader(message: string) {
    this.ariaLiveMessage = ''

    requestAnimationFrame(() => {
      this.ariaLiveMessage = message

      this.clearAriaLiveTimeout()
      this.ariaLiveTimeout = window.setTimeout(() => {
        this.ariaLiveMessage = ''
      }, this.ARIA_LIVE_CLEANUP_DELAY)
    })
  }

  /**
   * Anuncia mudança de etapa para leitores de tela.
   * Informa direção da navegação e progresso atual.
   * @param newStep - Nova etapa
   * @param oldStep - Etapa anterior
   */
  private announceStepChange(newStep: number, oldStep: number) {
    const totalSteps = this.panels.length

    let direction = ''
    if (newStep > oldStep) {
      direction = 'Avançando para'
    } else if (newStep < oldStep) {
      direction = 'Retornando para'
    }

    const message = `${direction}, etapa ${newStep} de ${totalSteps}`
    this.announceToScreenReader(message)
  }

  /**
   * Limpa o timeout de mensagens aria-live.
   */
  private clearAriaLiveTimeout() {
    if (this.ariaLiveTimeout !== null) {
      clearTimeout(this.ariaLiveTimeout)
      this.ariaLiveTimeout = null
    }
  }

  /**
   * Move foco para o primeiro elemento do painel ativo.
   *
   */
  private setFocusOnActivePanel() {
    requestAnimationFrame(() => {
      const activePanel = this.panels[this.currentStep - 1] as HTMLBrWizardPanelElement | undefined
      if (typeof activePanel === 'undefined') return

      const panelElement = activePanel.shadowRoot?.querySelector('.wizard-panel-content')
      if (typeof panelElement === 'undefined' || panelElement === null) return

      const focusableElements = panelElement.querySelectorAll(this.FOCUSABLE_SELECTOR)

      if (focusableElements.length > 0) {
        ;(focusableElements[0] as HTMLElement).focus()
      }
    })
  }

  // ========================================
  // DETECÇÃO MOBILE E RESPONSIVIDADE
  // ========================================

  /**
   * Configura detecção de viewport mobile.
   * Ativa listeners de resize e configura gestos swipe.
   */
  private setupMobileDetection() {
    this.isMobile = window.innerWidth <= 992
    window.addEventListener('resize', this.handleResize)

    if (this.isMobile) {
      this.setupSwipeListeners()
    }
  }

  /**
   * Remove listeners de detecção mobile.
   */
  private cleanupMobileDetection() {
    window.removeEventListener('resize', this.handleResize)
  }

  /**
   * Handler de resize da janela.
   * Alterna entre modos mobile/desktop e configura/limpa gestos swipe.
   */
  private handleResize = () => {
    const wasMobile = this.isMobile
    this.isMobile = window.innerWidth <= 992

    if (this.isMobile && !wasMobile) {
      this.collapsed = true
      this.setupSwipeListeners()
    } else if (!this.isMobile && wasMobile) {
      this.cleanupSwipeListeners()
      this.collapsed = false
    }
  }

  // ========================================
  // GESTOS SWIPE (MOBILE)
  // ========================================

  /**
   * Configura gestos swipe para mobile.
   */
  private setupSwipeListeners() {
    if (!this.isMobile) return

    requestAnimationFrame(() => {
      this.progressBarRef = this.el.shadowRoot?.querySelector('.wizard-progress') || null
      this.formRef = this.el.shadowRoot?.querySelector('.wizard-form') || null

      if (this.progressBarRef !== null) {
        const direction = this.orientation === 'horizontal' ? 'vertical' : 'horizontal'
        this.progressBarSwipe = new UniversalSwipe(this.progressBarRef, { direction })

        if (this.orientation === 'horizontal') {
          this.progressBarSwipe.on('SWIPE_DOWN', () => this.expandSteps())
          this.progressBarSwipe.on('SWIPE_UP', () => this.collapseSteps())
        } else {
          this.progressBarSwipe.on('SWIPE_RIGHT', () => this.expandSteps())
          this.progressBarSwipe.on('SWIPE_LEFT', () => this.collapseSteps())
        }
      }

      if (this.formRef !== null) {
        this.formRef.addEventListener('touchstart', this.handleFormTouchStart)
      }
    })
  }

  /**
   * Remove listeners de gestos swipe.
   */
  private cleanupSwipeListeners() {
    if (this.progressBarSwipe !== null) {
      this.progressBarSwipe.off('SWIPE_DOWN', this.expandSteps)
      this.progressBarSwipe.off('SWIPE_UP', this.collapseSteps)
      this.progressBarSwipe.off('SWIPE_RIGHT', this.expandSteps)
      this.progressBarSwipe.off('SWIPE_LEFT', this.collapseSteps)

      this.progressBarSwipe.destroy()
      this.progressBarSwipe = null
    }

    if (this.formRef !== null) {
      this.formRef.removeEventListener('touchstart', this.handleFormTouchStart)
    }
  }

  /**
   * Handler de toque no formulário.
   * Recolhe automaticamente a barra de progresso.
   */
  private handleFormTouchStart = () => {
    this.collapseSteps()
  }

  /**
   * Recolhe a barra de progresso (mobile).
   * Adiciona o atributo 'collapsed' ao host.
   */
  private collapseSteps() {
    if (this.isMobile) {
      this.collapsed = true
    }
  }

  /**
   * Expande a barra de progresso (mobile).
   * Remove o atributo 'collapsed' do host.
   */
  private expandSteps() {
    if (this.isMobile) {
      this.collapsed = false
    }
  }

  // ========================================
  // HANDLERS DE EVENTOS DE BOTÕES
  // ========================================

  /**
   * Handler do botão "Avançar" ou "Concluir".
   * Se for a última etapa, valida e emite evento de conclusão.
   * Caso contrário, avança para a próxima etapa.
   */
  private handleNext = async () => {
    if (this.currentStep === this.panels.length) {
      // Última etapa: validar antes de completar
      const eventResult = this.brWizardBeforeStepChange.emit(this.currentStep)

      // Se preventDefault() foi chamado, bloqueia conclusão
      if (!eventResult.defaultPrevented) {
        this.brWizardComplete.emit()
      }
    } else {
      await this.nextStep()
    }
  }

  /**
   * Handler do botão "Voltar".
   * Navega para a etapa anterior.
   */
  private handlePrevious = async () => {
    await this.previousStep()
  }

  /**
   * Handler do botão "Cancelar".
   * Emite evento de cancelamento do wizard.
   */
  private handleCancel = () => {
    this.brWizardCancel.emit()
  }

  // ========================================
  // MÉTODOS DE RENDERIZAÇÃO
  // ========================================

  /**
   * Retorna classes CSS do container principal.
   * @returns CssClassMap - Mapa de classes CSS
   */
  private getHostClassMap(): CssClassMap {
    return {
      'br-wizard': true,
    }
  }

  /**
   * Renderiza indicadores de progresso usando br-step.
   * @returns JSX.Element | null - Elemento de progresso ou null se não houver painéis
   */
  private renderProgress() {
    if (this.panels.length === 0) {
      return null
    }

    return (
      <div class="wizard-progress">
        <br-step
          ref={(el) => (this.stepRef = el)}
          initial-step={String(this.currentStep - 1)}
          layout={this.orientation}
          label-position={this.orientation === 'vertical' ? 'bottom' : 'bottom'}
          progression-type={this.progressionType}
          mode="step"
        >
          {this.panels.map((panel) => {
            const label = panel.getAttribute('label') || ''
            return <br-step-item label={label}></br-step-item>
          })}
        </br-step>
      </div>
    )
  }

  /**
   * Renderiza conteúdo dos painéis via slot.
   * @returns JSX.Element - Container com slot dos painéis
   */
  private renderPanelContent() {
    return (
      <div class="wizard-panel-content">
        <slot />
      </div>
    )
  }

  /**
   * Renderiza botões de navegação do wizard.
   * Adapta-se ao contexto: primeira etapa, última etapa, mobile/desktop.
   * @returns JSX.Element - Container com botões de navegação
   */
  private renderPanelButtons() {
    const isFirst = this.currentStep === 1
    const isLast = this.currentStep === this.panels.length

    const buttonShape = this.isMobile ? 'block' : undefined

    return (
      <div class="wizard-panel-btn">
        <br-button
          class="wizard-btn-canc"
          type="button"
          shape={buttonShape}
          emphasis="tertiary"
          onClick={this.handleCancel}
        >
          Cancelar
        </br-button>

        <br-button
          class={isLast ? 'wizard-btn' : 'wizard-btn-next'}
          emphasis="primary"
          type="button"
          shape={buttonShape}
          onClick={this.handleNext}
        >
          {isLast ? 'Concluir' : 'Avançar'}
        </br-button>

        {!isFirst && (
          <br-button
            class="wizard-btn-prev"
            emphasis="secondary"
            type="button"
            shape={buttonShape}
            onClick={this.handlePrevious}
          >
            Voltar
          </br-button>
        )}
      </div>
    )
  }

  /**
   * Renderiza região de anúncios para leitores de tela.
   * Usa aria-live para comunicar mudanças de estado de forma acessível.
   * @returns JSX.Element - Elemento sr-only com aria-live
   */
  private renderAriaLiveRegion() {
    return (
      <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {this.ariaLiveMessage}
      </div>
    )
  }

  /**
   * Método de renderização principal do componente.
   * Monta a estrutura completa do wizard com acessibilidade integrada.
   * @returns JSX.Element - Estrutura completa do wizard
   */
  render() {
    const hostClass = this.getHostClassMap()
    const verticalAttr = this.orientation === 'vertical' ? { vertical: '' } : {}
    const collapsedAttr = this.collapsed ? { collapsed: '' } : {}
    const activePanelAttr = { active: '' }

    // Atributos ARIA para o painel ativo
    const totalSteps = this.panels.length
    const currentPanel = this.panels[this.currentStep - 1]
    const stepLabel = currentPanel?.getAttribute('label') || `Etapa ${this.currentStep}`

    return (
      <Host class={hostClass} {...verticalAttr} {...collapsedAttr} step={String(this.currentStep)}>
        {this.renderAriaLiveRegion()}

        {this.renderProgress()}

        <div class="wizard-form" role="region" aria-label={`Wizard: ${stepLabel}`}>
          <div
            class="wizard-panel"
            {...activePanelAttr}
            role="group"
            aria-label={`${stepLabel}, etapa ${this.currentStep} de ${totalSteps}`}
          >
            {this.renderPanelContent()}
            {this.renderPanelButtons()}
          </div>
        </div>
      </Host>
    )
  }
}
